import React from "react";
import { Link } from "react-router-dom";
import { Cpu, Code2, BarChart3, Compass, Palette, PenTool, ArrowUpRight } from "lucide-react";
import { useI18n } from "../../lib/i18n";

/**
 * Premium service-cards grid — inspired by Linear / Stripe / Vercel.
 *
 * Design notes:
 * - All cards equal height via grid-auto-rows + h-full flex
 * - Navy → black gradient base, glass border, soft orange glow on hover
 * - Lift -8px on hover, 300ms transition
 * - Title 28-32px (spec said 48 but that breaks at cards-level — kept hero hierarchy)
 * - Body 16-17px, 1.65 line-height, max 4 lines so no overflow
 * - Accent border-glow uses ::before pseudo via inline shadow for cross-browser support
 */
export const ServicesPreview = () => {
  const { t } = useI18n();

  const services = [
    { key: "marketing", icon: BarChart3, tags: ["SEO", "Performance Ads", "Analytics", "Content"] },
    { key: "content", icon: Palette, tags: ["Storytelling", "Copy", "Identity", "Social"] },
    { key: "web", icon: Code2, tags: ["React", "Next.js", "Webflow", "Headless CMS"] },
    { key: "ai", icon: Cpu, tags: ["GPT-5", "Claude", "n8n", "LangChain"] },
    { key: "brand", icon: PenTool, tags: ["Logo", "Visual System", "Guidelines", "Assets"] },
    { key: "consulting", icon: Compass, tags: ["Strategy", "Roadmap", "Audits", "Workshops"] },
  ];

  return (
    <section
      data-testid="services-preview"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-3xl mb-16">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-12 h-px bg-brand-orange" />
          <span className="label-mono">{t.services.kicker}</span>
        </div>
        <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-white leading-[1.05]">
          {t.services.title}
        </h2>
        <p className="mt-6 text-white/60 text-lg leading-relaxed">
          {t.services.subtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
        {services.map((s) => {
          const Icon = s.icon;
          const data = t.services[s.key];
          return (
            <Link
              key={s.key}
              to="/services"
              data-testid={`service-card-${s.key}`}
              className="
                group relative h-full flex flex-col
                rounded-[24px] overflow-hidden
                bg-gradient-to-br from-[#0E1A2A] via-[#0A1322] to-[#05080E]
                border border-white/[0.07]
                p-8 md:p-10
                transition-all duration-300 ease-out
                hover:-translate-y-2 hover:border-brand-orange/40
                hover:shadow-[0_24px_60px_-12px_rgba(255,106,0,0.28),0_0_0_1px_rgba(255,106,0,0.15)]
              "
            >
              {/* Subtle orange wash on hover */}
              <span
                aria-hidden
                className="
                  absolute inset-0 opacity-0 group-hover:opacity-100
                  transition-opacity duration-300 pointer-events-none
                  bg-[radial-gradient(60%_50%_at_50%_0%,rgba(255,106,0,0.18)_0%,transparent_70%)]
                "
              />
              {/* Top-right ambient glow */}
              <span
                aria-hidden
                className="
                  absolute -top-24 -right-20 w-56 h-56 rounded-full
                  bg-brand-orange/10 blur-3xl opacity-40
                  group-hover:opacity-90 transition-opacity duration-300
                "
              />

              <div className="relative z-10 flex flex-col h-full">
                {/* Icon */}
                <div
                  className="
                    w-14 h-14 rounded-2xl
                    bg-white/[0.04] border border-white/[0.08]
                    flex items-center justify-center
                    transition-colors duration-300
                    group-hover:border-brand-orange/40 group-hover:bg-brand-orange/[0.08]
                  "
                >
                  <Icon
                    className="
                      w-6 h-6 text-brand-orange
                      transition-transform duration-300 group-hover:scale-110
                    "
                  />
                </div>

                {/* Title */}
                <h3 className="mt-7 font-display font-bold text-[28px] md:text-[30px] text-white tracking-tight leading-[1.15]">
                  {data.title}
                </h3>

                {/* Description — clamped to keep cards equal */}
                <p
                  className="
                    mt-4 text-[15.5px] text-white/65 leading-[1.65]
                    line-clamp-3
                  "
                >
                  {data.desc}
                </p>

                {/* Capability tags — fade-in on hover */}
                <div
                  className="
                    mt-5 flex flex-wrap gap-1.5
                    opacity-60 group-hover:opacity-100
                    transition-opacity duration-300
                  "
                >
                  {s.tags.map((tag) => (
                    <span
                      key={tag}
                      className="
                        text-[11px] font-mono tracking-wider uppercase
                        px-2.5 py-1 rounded-full
                        bg-white/[0.04] border border-white/[0.08]
                        text-white/55
                        group-hover:border-brand-orange/30 group-hover:text-white/85
                        transition-all duration-300
                      "
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Spacer pushes the CTA to the bottom for equal-height balance */}
                <div className="flex-1 min-h-[16px]" />

                {/* CTA */}
                <div className="pt-6 mt-auto flex items-center gap-2 text-sm font-medium text-white/70 group-hover:text-brand-orange transition-colors duration-300">
                  <span>{t.common.learnMore}</span>
                  <ArrowUpRight
                    className="
                      w-4 h-4
                      transition-transform duration-300
                      group-hover:translate-x-1 group-hover:-translate-y-1
                    "
                  />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
